import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function SuccessCard() {
  const themeColor = 'rgba(46, 125, 50, 1)';
  const API = process.env.REACT_APP_API_BASE_URL;

  const [cleared, setCleared] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const sid = typeof window !== 'undefined' ? localStorage.getItem('sessionID') : null;

    (async () => {
      try {
        // Call backend clear endpoint. Include session-id header if we have one,
        // and include credentials in case you store session on cookie.
        const res = await fetch(`${API}/cart/clear`, {
          method: 'DELETE',
          headers: {
            ...(sid ? { 'session-id': sid } : {}),
            'Content-Type': 'application/json'
          },
          credentials: 'include', // send cookies if your sessionID is cookie-based
          signal: controller.signal
        });

        if (!res.ok) {
          // attempt to read json or text for a helpful message
          let msg = 'Failed to clear cart';
          try {
            const body = await res.json();
            msg = body?.message || JSON.stringify(body);
          } catch (e) {
            try { msg = await res.text(); } catch(e) {}
          }
          throw new Error(msg);
        }

        // Success: clear client-side cart state too
        localStorage.removeItem('cart'); // your app's cart key (adjust if different)
        // Optionally remove sessionID if you don't need it after checkout:
        // localStorage.removeItem('sessionID');

        setCleared(true);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Clear cart error:', err);
        setError(err.message || 'Failed to clear cart locally — webhook will still finalize server-side.');
      }
    })();

    return () => controller.abort();
  }, [API]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50/50 via-white to-green-100/50 flex items-center justify-center p-6 font-[Poppins]">
      <motion.div
        className="bg-white border border-slate-100 p-10 rounded-[2.5rem] shadow-[0_20px_50px_rgba(21,128,61,0.05)] text-center max-w-md w-full space-y-6 flex flex-col items-center"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <motion.div
          className="rounded-full w-36 h-36 mx-auto flex items-center justify-center shadow-inner relative"
          style={{ backgroundColor: 'rgba(21, 128, 61, 0.08)' }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 120 }}
        >
          {/* Decorative halo ring */}
          <div className="absolute inset-[-8px] rounded-full border border-green-700/10 animate-pulse" />
          
          <motion.i
            className="bx bx-check text-7xl"
            style={{ color: themeColor }}
            initial={{ rotate: -180, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          />
        </motion.div>

        <div className="space-y-2">
          <motion.h1
            className="text-3xl font-black font-[Montserrat] tracking-tight"
            style={{ color: themeColor }}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            Order Placed!
          </motion.h1>

          <motion.p
            className="text-slate-600 text-sm sm:text-base leading-relaxed font-medium"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            We received your purchase request.
            <br />We will keep you updated on your tracking!
          </motion.p>
        </div>

        {/* Action Link button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="pt-2 w-full"
        >
          <Link
            to="/"
            className="w-full py-3 bg-green-700 hover:bg-green-800 text-white rounded-full font-bold shadow-md hover:shadow-lg transition-all text-sm flex items-center justify-center gap-1.5"
          >
            <i className="bx bx-home text-base" /> Back to Home
          </Link>
          
          <Link
            to="/track"
            className="mt-3 block text-green-700 hover:text-green-800 text-xs font-bold transition-all hover:underline"
          >
            <i className="bx bx-receipt text-xs" /> Track Order Status
          </Link>
        </motion.div>

        {/* Status message about cart clearing */}
        <div className="border-t border-slate-50 pt-4 w-full text-xs">
          {cleared && (
            <div className="text-green-700 font-bold bg-green-50 py-1.5 px-3 rounded-full inline-block border border-green-100">
              Cart cleared successfully ✓
            </div>
          )}
          {!cleared && !error && (
            <div className="text-slate-400 font-semibold flex items-center justify-center gap-1.5">
              <i className="bx bx-loader-alt animate-spin text-sm" />
              <span>Finalizing order...</span>
            </div>
          )}
          {error && (
            <div className="text-red-700 font-semibold bg-red-50 py-1.5 px-3 rounded-full inline-block border border-red-100">
              {error}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
